import { makeStyles, Text, Title2, Title3 } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { dolphinTokens } from "../../../utils/Theme";
import Map from "../../../components/map/Map";
import Status from "../../../enums/Status";
import addressService from "../../../services/AddressService";
import IAddress from "../../../models/localisation/IAddress";
import { useEffect, useState } from "react";
import { formatAddressMultiline, formatDetails, formatStreet } from "../../../utils/Address";

const useStyles = makeStyles({
    section: {
        padding: "2rem 0",
        backgroundColor: dolphinTokens.colorDarkBlue20,
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    content: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: "1200px",

        "@media (max-width: 768px)": {
            flexDirection: "column",
            alignItems: "center"
        }
    },
    mapPlaceholder: {
        width: "40%",
        height: "400px"
    },
    locationInfo: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        marginLeft: "2rem",
        width: "50%",

        "@media (max-width: 768px)": {
            width: "100%",
            marginLeft: "0",
            marginTop: "2rem",
            alignItems: "center",
            textAlign: "center"
        }
    },
    locationTitle: {
        fontSize: "2rem",
        fontWeight: 600,
        marginBottom: "1rem",
        color: "#333",
        lineHeight: "1.2",

        "& .highlight": {
            color: dolphinTokens.colorLightBlue
        }
    },
    locationText: {
        fontSize: "1rem",
        marginBottom: "2rem"
    },
    statsContainer: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: "100%",
        
        "@media (max-width: 768px)": {
            justifyContent: "space-around"
        }
    },
    statItem: {
        textAlign: "left",
        maxWidth: "33%",

        "& p": {
            fontSize: "1rem",
            margin: 0,
            fontWeight: 400
        },

        "@media (max-width: 768px)": {
            textAlign: "center"
        }
    },
    statNumber: {
        fontSize: "2rem",
        fontWeight: 600,
        color: dolphinTokens.colorLightBlue,
        marginBottom: "0.5rem"
    }
});

const Stat = (props: { number: number; text: string }) => {
    const { number, text } = props;
    const styles = useStyles();

    return (
        <div className={styles.statItem}>
            <Title3 block className={styles.statNumber}>{number}+</Title3>
            <Text block as="p">{text}</Text>
        </div>
    );
}

const LocationMap = () => {
    const { t } = useTranslation("contact");
    const styles = useStyles();

    const [address, setAddress] = useState<IAddress>(null!);
    const [status, setStatus] = useState<Status>(Status.LOADING);

    useEffect(() => {
        fetchAddress(setAddress, setStatus);
    }, []);

    if ([Status.LOADING, Status.LOAD_ERROR].includes(status) || !address) {
        return <div className={styles.mapPlaceholder} />;
    }

    const addressMultiline = formatAddressMultiline(address);

    return (
        <Map
            coordinates={[address.lat, address.long]}
            zoom={16}
            marker={{
                color: dolphinTokens.colorLightBlue,
                position: [address.lat, address.long]
            }}
            popup={{
                content: addressMultiline,
                closeButton: true,
            }}
            isRounded={true}
            />
    );
};

export default function Location() {
    const { t } = useTranslation("ourstory");
    const styles = useStyles();
    
    return (
        <section className={styles.section}>
            <div className={styles.content}>
                <LocationMap />
                <div className={styles.locationInfo}>
                    <Title2 block as="h2" className={styles.locationTitle}>
                        <span dangerouslySetInnerHTML={{ __html: t("location.title") }} />
                    </Title2>
                    <Text block as="p" className={styles.locationText}>
                        {t("location.description")}
                    </Text>
                    <div className={styles.statsContainer}>
                        <Stat number={10} text={t("location.stats.experience")} />
                        <Stat number={100} text={t("location.stats.clients")} />
                        <Stat number={500} text={t("location.stats.tickets")} />
                    </div>
                </div>
            </div>
        </section>
    );
}

async function fetchAddress (
    setAddress: (address: IAddress) => void,
    setStatus: (status: Status) => void
) {
    try {
        const address = await addressService.getLocalAddress();
        
        setAddress(address);
        setStatus(Status.LOAD_SUCCESS);
    } catch (error) {
        setStatus(Status.LOAD_ERROR);
    }
}